import React, { useState, useEffect } from 'react';
import { Plus, FileText, Video, Mic, Brain, Sparkles, Zap, Mail, Calendar, MessageSquare, BookOpen, ArrowRight, CheckCircle, BarChart3, Zap as ZapIcon, Sparkles as SparklesIcon, X, Megaphone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Post } from '../../types';

interface CreateExperienceProps {
  onStartCreating: (type: CreateType, template?: any) => void;
  onClose: () => void;
}

type CreateType = 'newsletter' | 'article' | 'series' | 'announcement' | 'poll' | 'event' | 'media' | 'ai-generated';

interface CreateOption {
  id: CreateType;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  gradient: string;
  features: string[];
  estimatedTime: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  engagement: 'high' | 'medium' | 'low';
  templates?: any[];
}

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  action: () => void;
  color: string;
}

const CreateExperience: React.FC<CreateExperienceProps> = ({ onStartCreating, onClose }) => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'templates' | 'quick' | 'ai' | 'series'>('templates');
  const [selectedType, setSelectedType] = useState<CreateType | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [aiSuggestions, setAiSuggestions] = useState<any[]>([]);

  const createOptions: CreateOption[] = [
    {
      id: 'newsletter',
      title: 'Newsletter',
      description: 'Create a weekly newsletter with engagement features',
      icon: Mail,
      color: 'blue',
      gradient: 'from-blue-500 to-blue-600',
      features: ['Email templates', 'Subscriber management', 'Analytics', 'Social sharing'],
      estimatedTime: '15-30 min',
      difficulty: 'beginner',
      engagement: 'high',
      templates: [
        {
          id: 'weekly-update',
          name: 'Weekly Update',
          description: 'Perfect for regular newsletters',
          preview: 'Weekly Update Template',
          engagement: 'high'
        },
        {
          id: 'announcement',
          name: 'Announcement',
          description: 'For important updates and news',
          preview: 'Announcement Template',
          engagement: 'high'
        }
      ]
    },
    {
      id: 'article',
      title: 'Article',
      description: 'Write a standalone article with rich formatting',
      icon: FileText,
      color: 'green',
      gradient: 'from-green-500 to-green-600',
      features: ['Rich editor', 'SEO optimization', 'Comments', 'Social sharing'],
      estimatedTime: '30-60 min',
      difficulty: 'intermediate',
      engagement: 'medium',
      templates: [
        {
          id: 'how-to',
          name: 'How-to Guide',
          description: 'Step-by-step tutorial format',
          preview: 'How-to Template',
          engagement: 'high'
        },
        {
          id: 'opinion',
          name: 'Opinion Piece',
          description: 'Personal take on trending topics',
          preview: 'Opinion Template',
          engagement: 'high'
        }
      ]
    },
    {
      id: 'series',
      title: 'Series',
      description: 'Create a multi-part content series',
      icon: BookOpen,
      color: 'purple',
      gradient: 'from-purple-500 to-purple-600',
      features: ['Episode planning', 'Progress tracking', 'Subscriber notifications', 'Series analytics'],
      estimatedTime: '2-4 hours',
      difficulty: 'advanced',
      engagement: 'high',
      templates: [
        {
          id: 'tutorial-series',
          name: 'Tutorial Series',
          description: 'Multi-part educational content',
          preview: 'Tutorial Series Template',
          engagement: 'high'
        }
      ]
    },
    {
      id: 'announcement',
      title: 'Announcement',
      description: 'Share important updates with your audience',
      icon: Megaphone,
      color: 'orange',
      gradient: 'from-orange-500 to-orange-600',
      features: ['Urgent delivery', 'High visibility', 'Engagement tracking', 'Social amplification'],
      estimatedTime: '10-20 min',
      difficulty: 'beginner',
      engagement: 'high'
    },
    {
      id: 'poll',
      title: 'Poll & Survey',
      description: 'Engage your audience with interactive content',
      icon: BarChart3,
      color: 'indigo',
      gradient: 'from-indigo-500 to-indigo-600',
      features: ['Interactive polls', 'Real-time results', 'Data collection', 'Audience insights'],
      estimatedTime: '15-30 min',
      difficulty: 'beginner',
      engagement: 'high'
    },
    {
      id: 'event',
      title: 'Event',
      description: 'Promote and manage events',
      icon: Calendar,
      color: 'red',
      gradient: 'from-red-500 to-red-600',
      features: ['Event details', 'RSVP tracking', 'Reminder emails', 'Post-event follow-up'],
      estimatedTime: '20-40 min',
      difficulty: 'intermediate',
      engagement: 'high'
    },
    {
      id: 'media',
      title: 'Media Content',
      description: 'Create audio, video, or visual content',
      icon: Video,
      color: 'pink',
      gradient: 'from-pink-500 to-pink-600',
      features: ['Media upload', 'Audio recording', 'Video editing', 'Visual storytelling'],
      estimatedTime: '45-90 min',
      difficulty: 'intermediate',
      engagement: 'high'
    },
    {
      id: 'ai-generated',
      title: 'AI Generated',
      description: 'Let AI help you create content',
      icon: Brain,
      color: 'teal',
      gradient: 'from-teal-500 to-teal-600',
      features: ['AI writing assistant', 'Content suggestions', 'Auto-formatting', 'Smart templates'],
      estimatedTime: '10-30 min',
      difficulty: 'beginner',
      engagement: 'medium'
    }
  ];

  const quickActions: QuickAction[] = [
    {
      id: 'voice-to-text',
      title: 'Voice to Text',
      description: 'Speak your content and let AI transcribe it',
      icon: Mic,
      action: () => onStartCreating('ai-generated', { type: 'voice-to-text' }),
      color: 'blue'
    },
    {
      id: 'ai-rewrite',
      title: 'AI Rewrite',
      description: 'Improve existing content with AI',
      icon: Sparkles,
      action: () => onStartCreating('ai-generated', { type: 'rewrite' }),
      color: 'purple'
    },
    {
      id: 'ai-newsletter',
      title: 'AI Newsletter',
      description: 'Generate a complete newsletter with AI',
      icon: Mail,
      action: () => onStartCreating('ai-generated', { 
        type: 'generate', 
        contentType: 'newsletter',
        topic: 'Weekly Update',
        tone: 'professional',
        length: 'medium'
      }),
      color: 'blue'
    },
    {
      id: 'ai-article',
      title: 'AI Article',
      description: 'Create an engaging article with AI assistance',
      icon: FileText,
      action: () => onStartCreating('ai-generated', { 
        type: 'generate', 
        contentType: 'article',
        topic: 'Your Topic',
        tone: 'professional',
        length: 'long'
      }),
      color: 'green'
    },
    {
      id: 'quick-announcement',
      title: 'Quick Announcement',
      description: 'Send an urgent update to subscribers',
      icon: Zap,
      action: () => onStartCreating('announcement'),
      color: 'orange'
    },
    {
      id: 'engagement-poll',
      title: 'Engagement Poll',
      description: 'Ask your audience a question',
      icon: MessageSquare,
      action: () => onStartCreating('poll'),
      color: 'green'
    }
  ];

  const tabs = [
    { id: 'templates', label: 'Templates', icon: FileText },
    { id: 'quick', label: 'Quick Actions', icon: Zap },
    { id: 'ai', label: 'AI Assistant', icon: Brain },
    { id: 'series', label: 'Series', icon: BookOpen }
  ];

  useEffect(() => {
    // Load recent activity and AI suggestions
    loadRecentActivity();
    loadAISuggestions();
  }, []);

  const loadRecentActivity = async () => {
    // Mock recent activity
    setRecentActivity([
      {
        id: '1',
        type: 'newsletter',
        title: 'Weekly Tech Update',
        createdAt: new Date(),
        engagement: 85
      },
      {
        id: '2',
        type: 'article',
        title: 'The Future of AI',
        createdAt: new Date(Date.now() - 86400000),
        engagement: 92
      }
    ]);
  };

  const loadAISuggestions = async () => {
    // Mock AI suggestions
    setAiSuggestions([
      {
        id: '1',
        title: 'Trending: AI in Healthcare',
        description: 'Based on your audience interests',
        confidence: 85,
        type: 'trending'
      },
      {
        id: '2',
        title: 'Follow-up: Previous Article',
        description: 'Continue the conversation from last week',
        confidence: 78,
        type: 'follow-up'
      }
    ]);
  };

  const handleCreateType = (type: CreateType) => {
    setSelectedType(type);
    
    // Check if this content type has templates
    const option = createOptions.find(opt => opt.id === type);
    if (option?.templates && option.templates.length > 0) {
      setShowTemplates(true);
    } else {
      // If no templates, start creating directly
      onStartCreating(type);
    }
  };

  const handleTemplateSelect = (template: any) => {
    onStartCreating(selectedType!, template);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getEngagementColor = (engagement: string) => {
    switch (engagement) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-none shadow-2xl w-full h-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create Content</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Choose how you want to create your next piece of content
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1 sm:gap-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
          {activeTab === 'templates' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {createOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <div
                      key={option.id}
                      onClick={() => handleCreateType(option.id)}
                      className="group cursor-pointer bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all duration-200 hover:border-blue-300 dark:hover:border-blue-600"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${option.gradient} flex items-center justify-center`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(option.difficulty)}`}>
                            {option.difficulty}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEngagementColor(option.engagement)}`}>
                            {option.engagement} engagement
                          </span>
                        </div>
                      </div>
                      
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {option.title}
                      </h3>
                      
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                        {option.description}
                      </p>
                      
                      <div className="space-y-2 mb-4">
                        {option.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            {feature}
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                        <span>⏱️ {option.estimatedTime}</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'quick' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quickActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.id}
                      onClick={action.action}
                      className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all duration-200 text-left group"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-${action.color}-100 dark:bg-${action.color}-900/20 flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 text-${action.color}-600 dark:text-${action.color}-400`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white">{action.title}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{action.description}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform ml-auto" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Brain className="w-6 h-6 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Content Assistant</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Let AI help you create content based on trending topics, your audience interests, and your content history.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {aiSuggestions.map((suggestion) => (
                    <div 
                      key={suggestion.id} 
                      onClick={() => onStartCreating('ai-generated', { 
                        type: 'generate', 
                        contentType: 'article',
                        topic: suggestion.title,
                        tone: 'professional',
                        length: 'medium'
                      })}
                      className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 cursor-pointer group"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">{suggestion.title}</h4>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{suggestion.confidence}% match</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{suggestion.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 group-hover:underline">
                          Use this suggestion →
                        </span>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'series' && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Create a Series</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Plan and create multi-part content that keeps your audience engaged
                </p>
                <button 
                  onClick={() => onStartCreating('series')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Start Planning Series
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Templates Modal */}
        {showTemplates && selectedType && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-60">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Choose a Template
                  </h3>
                  <button
                    onClick={() => setShowTemplates(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Start from Scratch Option */}
                  <div
                    onClick={() => onStartCreating(selectedType!)}
                    className="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center justify-center mb-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <Plus className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </div>
                    </div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2 text-center">Start from Scratch</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 text-center">Create your own content without a template</p>
                    <div className="flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                  
                  {/* Template Options */}
                  {createOptions.find(opt => opt.id === selectedType)?.templates?.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all duration-200 cursor-pointer group"
                    >
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">{template.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{template.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{template.preview}</span>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateExperience;
